import {FC, useMemo} from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {Box, IconButton, Switch} from '@mui/material';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  ManagedMethod,
  ManagedMethodKind,
  useGetManagedMethods,
  useReorderManagedMethods,
  useUpdateManagedMethod,
} from '@entities/paymentMethod';
import {getErrorMessageFromApiError} from '@shared/api';
import {MrTable} from '@shared/components';
import {entityTableCommonProps} from '@shared/constants';

import {COLOR__PRIMARY, COLOR__WHITE} from '@/theme/colors';

// The app-wide theme keeps switch tracks grey even when checked; here an
// enabled method should read as clearly "on", so tint the checked state blue.
const enabledSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: COLOR__WHITE,
    '& + .MuiSwitch-track': {backgroundColor: COLOR__PRIMARY},
  },
} as const;

const getApiErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? getErrorMessageFromApiError(err) : '';

// Display order: highest sort_order first, name as a stable tie-breaker.
const byDisplayOrder = (a: ManagedMethod, b: ManagedMethod): number =>
  b.sortOrder - a.sortOrder || a.name.localeCompare(b.name);

export const ManagedMethodsTable: FC<{kind: ManagedMethodKind}> = ({kind}) => {
  const {enqueueSnackbar} = useSnackbar();

  const {data = [], isPending, error} = useGetManagedMethods(kind);
  const {mutate: updateMethod, isPending: isUpdating} =
    useUpdateManagedMethod(kind);
  const {mutate: reorderMethods, isPending: isReordering} =
    useReorderManagedMethods(kind);

  const isBusy = isUpdating || isReordering;

  const orderedMethods = useMemo(() => [...data].sort(byDisplayOrder), [data]);

  const handleToggleEnabled = (method: ManagedMethod, enabled: boolean) => {
    updateMethod(
      {id: method.id, enabled},
      {
        onSuccess: () => {
          enqueueSnackbar(
            `${method.name} ${enabled ? 'enabled' : 'disabled'}`,
            {variant: 'success'},
          );
        },
        onError: err => {
          enqueueSnackbar(
            `Could not update ${method.name}. ${getApiErrorMessage(err)}`,
            {variant: 'error'},
          );
        },
      },
    );
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= orderedMethods.length) {
      return;
    }

    const moved = orderedMethods[index];
    const displaced = orderedMethods[targetIndex];
    if (!moved || !displaced) {
      return;
    }

    const next = [...orderedMethods];
    next[index] = displaced;
    next[targetIndex] = moved;

    reorderMethods(next, {
      onError: err => {
        enqueueSnackbar(
          `Could not reorder methods. ${getApiErrorMessage(err)}`,
          {variant: 'error'},
        );
      },
    });
  };

  const columns = useMemo<MRT_ColumnDef<ManagedMethod>[]>(
    () => [
      {
        id: 'order',
        header: 'Order',
        size: 90,
        enableSorting: false,
        Cell: ({row}) => (
          <Box display="flex">
            <IconButton
              size="small"
              disabled={isBusy || row.index === 0}
              onClick={() => handleMove(row.index, 'up')}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={isBusy || row.index === orderedMethods.length - 1}
              onClick={() => handleMove(row.index, 'down')}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
      {accessorKey: 'name', header: 'Name'},
      {accessorKey: 'provider', header: 'Provider'},
      {accessorKey: 'currency', header: 'Currency', size: 100},
      {accessorKey: 'tokenCode', header: 'Token', size: 120},
      {
        accessorKey: 'country',
        header: 'Country',
        size: 100,
        Cell: ({row}) => row.original.country || '—',
      },
      {
        id: 'enabled',
        header: 'Enabled',
        size: 110,
        enableSorting: false,
        Cell: ({row}) => (
          <Switch
            checked={row.original.enabled}
            disabled={isBusy}
            sx={enabledSwitchSx}
            onChange={(_, checked) =>
              handleToggleEnabled(row.original, checked)
            }
          />
        ),
      },
    ],
    // handleMove / handleToggleEnabled are stable enough for this table
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBusy, orderedMethods],
  );

  return (
    <MrTable
      columns={columns}
      data={orderedMethods}
      error={error}
      enableSorting={false}
      state={{isLoading: isPending, showProgressBars: isReordering}}
      {...entityTableCommonProps}
      enableRowActions={false}
    />
  );
};
